import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { getStudents, createStudent, deleteStudent } from '../services/api';
import { exportTableToPDF } from '../utils/pdfExport';
import EmptyState from '../components/EmptyState';

const statusColors: Record<string, string> = {
  candidate: 'badge-blue',
  approved: 'badge-gold',
  active: 'badge-green',
  frozen: 'badge-yellow',
  graduated: 'badge-gray',
};

export default function Students() {
  const { t, lang } = useLang();
  const { userName } = useUser();
  const [students, setStudents] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', firstNameAr: '', lastNameAr: '',
    email: '', phone: '', address: '', city: '', program: '', dateOfBirth: '', nationalId: '', status: 'candidate',
    performedBy: ''
  });

  const loadStudents = () => {
    const params: any = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    getStudents(params).then(res => setStudents(res.data)).catch(() => {});
  };

  useEffect(() => { loadStudents(); }, [search, statusFilter]);

  const handleAdd = async () => {
    await createStudent(form);
    setShowModal(false);
    setForm({ firstName: '', lastName: '', firstNameAr: '', lastNameAr: '', email: '', phone: '', address: '', city: '', program: '', dateOfBirth: '', nationalId: '', status: 'candidate', performedBy: userName });
    loadStudents();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('האם למחוק?')) {
      await deleteStudent(id);
      loadStudents();
    }
  };

  const statusText = (s: string) => {
    const map: Record<string, string> = {
      candidate: t('candidate'), approved: t('approved'), active: t('active'),
      frozen: t('frozen'), graduated: t('graduated')
    };
    return map[s] || s;
  };

  const getName = (s: any) => {
    if (lang === 'ar' && s.firstNameAr) return `${s.firstNameAr} ${s.lastNameAr}`;
    return `${s.firstName} ${s.lastName}`;
  };

  return (
    <div>
      <div className="page-title">
        <span className="page-title-text">🎓 {t('studentList')}</span>
        <button className="btn btn-primary" onClick={() => { setForm({...form, performedBy: userName}); setShowModal(true); }}>
          + {t('addStudent')}
        </button>
      </div>

      <div className="page-section">
        <div className="toolbar">
          <input
            className="search-input"
            placeholder={t('search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="form-select"
            style={{ width: 'auto', minWidth: 150 }}
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">{t('all')}</option>
            <option value="candidate">{t('candidate')}</option>
            <option value="approved">{t('approved')}</option>
            <option value="active">{t('active')}</option>
            <option value="frozen">{t('frozen')}</option>
            <option value="graduated">{t('graduated')}</option>
          </select>
          <button className="btn btn-outline">{t('exportExcel')} 📥</button>
          <button
            className="btn"
            style={{ backgroundColor: '#B71C1C', color: '#fff', border: 'none' }}
            onClick={() => {
              const headers = ['#', t('name'), t('nationalId'), t('city'), t('program'), t('phone'), t('email'), t('status')];
              const rows = students.map((s, i) => [
                String(i + 1), getName(s), s.nationalId || '-', s.city || '-',
                s.program || '-', s.phone, s.email, statusText(s.status)
              ]);
              const date = new Date().toISOString().split('T')[0];
              exportTableToPDF(t('studentList'), headers, rows, `students_${date}.pdf`);
            }}
          >{t('exportPDF')} 📄</button>
        </div>

        <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('name')}</th>
              <th>{t('nationalId')}</th>
              <th>{t('city')}</th>
              <th>{t('program')}</th>
              <th>{t('phone')}</th>
              <th>{t('email')}</th>
              <th>{t('status')}</th>
              <th>{t('performedBy')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {students.map((s, i) => (
              <tr key={s.id}>
                <td>{i + 1}</td>
                <td>
                  <Link to={`/students/${s.id}`} style={{ color: '#7B1FA2', fontWeight: 600, textDecoration: 'none' }}>
                    {getName(s)}
                  </Link>
                </td>
                <td>{s.nationalId || '-'}</td>
                <td>{s.city || '-'}</td>
                <td>{s.program || '-'}</td>
                <td dir="ltr">{s.phone}</td>
                <td dir="ltr">{s.email}</td>
                <td><span className={`badge ${statusColors[s.status] || 'badge-gray'}`}>{statusText(s.status)}</span></td>
                <td>{s.performedBy || '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Link to={`/students/${s.id}`} className="btn btn-sm btn-outline">{t('details')}</Link>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(s.id)}>{t('delete')}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {students.length === 0 && (
          <EmptyState
            icon="🎓"
            title={t('emptyStudents')}
            actionLabel={t('addStudent')}
            onAction={() => { setForm({...form, performedBy: userName}); setShowModal(true); }}
          />
        )}
      </div>

      {/* Add Student Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('addStudent')}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('firstName')} (עברית)</label>
                <input className="form-input" value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('lastName')} (עברית)</label>
                <input className="form-input" value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('firstName')} (ערבית)</label>
                <input className="form-input" value={form.firstNameAr} onChange={e => setForm({...form, firstNameAr: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('lastName')} (ערבית)</label>
                <input className="form-input" value={form.lastNameAr} onChange={e => setForm({...form, lastNameAr: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('email')}</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('phone')}</label>
                <input className="form-input" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('address')}</label>
              <input className="form-input" value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('city')}</label>
                <input className="form-input" value={form.city} onChange={e => setForm({...form, city: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('program')}</label>
                <input className="form-input" value={form.program} onChange={e => setForm({...form, program: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('dateOfBirth')}</label>
                <input className="form-input" type="date" value={form.dateOfBirth} onChange={e => setForm({...form, dateOfBirth: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('nationalId')}</label>
                <input className="form-input" value={form.nationalId} onChange={e => setForm({...form, nationalId: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('status')}</label>
                <select className="form-select" value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                  <option value="candidate">{t('candidate')}</option>
                  <option value="approved">{t('approved')}</option>
                  <option value="active">{t('active')}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('actionPerformer')}</label>
                <input className="form-input" value={form.performedBy} onChange={e => setForm({...form, performedBy: e.target.value})} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleAdd}>{t('save')}</button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
